import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Bell, Music, Heart, Check, Loader2, X, Users2, Building2,
  MessageSquare, ArrowLeft, Send, Info,
} from "lucide-react";
import api from "../utils/api";

const TYPE_META = {
  new_release:   { icon: Music,          color: "#ec4899" },
  new_follower:  { icon: Heart,          color: "#3b82f6" },
  collab:        { icon: Users2,         color: "#60a5fa" },
  label_request: { icon: Building2,      color: "#34d399" },
  new_message:   { icon: MessageSquare,  color: "#60a5fa" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60)    return "Gerade eben";
  if (diff < 3600)  return `Vor ${Math.floor(diff / 60)} Min.`;
  if (diff < 86400) return `Vor ${Math.floor(diff / 3600)} Std.`;
  return `Vor ${Math.floor(diff / 86400)} Tagen`;
}

// ── Push-Benachrichtigung Item ─────────────────────────────────────────────────
function UserNotifItem({ n, onRead, onClose }) {
  const [expanded, setExpanded] = useState(false);
  const Meta = TYPE_META[n.type] || { icon: Bell, color: "#52525b" };
  const Icon = Meta.icon;
  const isLong = (n.message?.length ?? 0) > 60;

  return (
    <div
      className="px-4 py-3 transition-all duration-150 cursor-pointer select-none"
      style={{
        background: n.read ? "transparent" : "rgba(236,72,153,0.03)",
        borderBottom: "1px solid rgba(255,255,255,0.03)",
      }}
      onClick={() => { onRead(n.id); setExpanded(v => !v); }}
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{ background: `${Meta.color}18`, border: `1px solid ${Meta.color}25` }}>
          <Icon size={14} style={{ color: Meta.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className={`text-xs font-semibold leading-snug ${n.read ? "text-zinc-400" : "text-white"}`}>{n.title}</p>
            {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-rose-400 flex-shrink-0 mt-1" />}
          </div>
          <p className={`text-zinc-500 text-[11px] mt-0.5 ${expanded ? "whitespace-pre-wrap break-words" : "line-clamp-2"}`}>
            {n.message}
          </p>
          {isLong && (
            <span className="text-rose-500/60 text-[10px] mt-0.5 inline-block">
              {expanded ? "▲ weniger" : "▼ mehr lesen"}
            </span>
          )}
          {expanded && n.link && (
            <Link to={n.link} onClick={e => { e.stopPropagation(); onClose(); }}
              className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-rose-400 hover:text-rose-300 transition-colors">
              Ansehen →
            </Link>
          )}
          <p className="text-zinc-700 text-[10px] mt-1">{timeAgo(n.created_at)}</p>
        </div>
      </div>
    </div>
  );
}

// ── Thread-Listeneintrag ───────────────────────────────────────────────────────
function ThreadItem({ thread, onClick }) {
  return (
    <button
      onClick={() => onClick(thread)}
      data-testid={`thread-item-${thread.id}`}
      className="w-full px-4 py-3 text-left transition-colors hover:bg-white/[0.03] flex items-start gap-3"
      style={{ borderBottom: "1px solid rgba(255,255,255,0.03)" }}
    >
      <div className="relative flex-shrink-0">
        <div className="w-9 h-9 rounded-full overflow-hidden bg-zinc-800 flex items-center justify-center">
          {thread.other_avatar
            ? <img src={thread.other_avatar} alt="" className="w-full h-full object-cover" />
            : <span className="text-xs font-bold text-zinc-500">{thread.other_name?.[0]?.toUpperCase()}</span>}
        </div>
        {thread.other_is_label && (
          <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-emerald-600 flex items-center justify-center">
            <Building2 size={9} className="text-white" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-semibold text-white truncate">{thread.other_name}</p>
          <span className="text-[10px] text-zinc-600 flex-shrink-0">{timeAgo(thread.last_message_at)}</span>
        </div>
        <p className="text-zinc-500 text-[11px] truncate mt-0.5">{thread.last_message || "…"}</p>
        {thread.context_label && (
          <p className="text-[10px] text-zinc-700 truncate mt-0.5">
            {thread.context_type === "label_request" ? "🏷 " : "🤝 "}{thread.context_label}
          </p>
        )}
      </div>
      {thread.unread > 0 && (
        <span className="flex-shrink-0 min-w-[18px] h-[18px] rounded-full flex items-center justify-center text-[10px] font-black text-white px-1 mt-1"
          style={{ background: "linear-gradient(135deg,#3b82f6,#1d4ed8)" }}>
          {thread.unread > 9 ? "9+" : thread.unread}
        </span>
      )}
    </button>
  );
}

// ── Thread-Detailansicht ───────────────────────────────────────────────────────
function ThreadView({ threadId, onBack, onClose, currentUserId }) {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending]     = useState(false);
  const [showNote, setShowNote]   = useState(false);
  const messagesEndRef            = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/messages/threads/${threadId}`);
      setData(res.data);
      if (res.data.availability_note) setShowNote(true);
    } catch {}
    setLoading(false);
  }, [threadId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (data && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [data]);

  const sendReply = async () => {
    if (!replyText.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/messages/threads/${threadId}/reply`, { text: replyText.trim() });
      setReplyText("");
      await load();
    } catch {}
    setSending(false);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-white/[0.06] flex-shrink-0">
        <button onClick={onBack} className="p-1 rounded-lg hover:bg-white/[0.06] transition-colors text-zinc-400 hover:text-white">
          <ArrowLeft size={14} />
        </button>
        {data && (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-7 h-7 rounded-full overflow-hidden bg-zinc-800 flex-shrink-0 flex items-center justify-center">
              {data.other_info.avatar
                ? <img src={data.other_info.avatar} alt="" className="w-full h-full object-cover" />
                : <span className="text-[10px] font-bold text-zinc-500">{data.other_info.name?.[0]?.toUpperCase()}</span>}
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold text-white truncate">{data.other_info.name}</p>
              {data.context_label && (
                <p className="text-[10px] text-zinc-600 truncate">{data.context_label}</p>
              )}
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 size={16} className="animate-spin text-zinc-700" />
        </div>
      ) : data ? (
        <>
          {/* Verfügbarkeitsnotiz */}
          {data.availability_note && (
            <div className="mx-3 mt-2 px-3 py-2 rounded-xl flex items-start gap-2 flex-shrink-0"
              style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.15)" }}>
              <Info size={11} className="text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-amber-300/80 text-[11px] leading-relaxed">{data.availability_note}</p>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2" style={{ scrollbarWidth: "none" }}>
            {data.messages.map(m => (
              <div key={m.id} className={`flex ${m.is_mine ? "justify-end" : "justify-start"} gap-2`}>
                {!m.is_mine && (
                  <div className="w-6 h-6 rounded-full overflow-hidden bg-zinc-800 flex-shrink-0 flex items-center justify-center mt-auto">
                    {m.sender_avatar
                      ? <img src={m.sender_avatar} alt="" className="w-full h-full object-cover" />
                      : <span className="text-[9px] font-bold text-zinc-500">{m.sender_name?.[0]?.toUpperCase()}</span>}
                  </div>
                )}
                <div className={`max-w-[85%] ${m.is_mine ? "items-end" : "items-start"} flex flex-col`}>
                  <div
                    className="px-3 py-2 rounded-2xl text-xs leading-relaxed break-words"
                    style={m.is_mine
                      ? { background: "rgba(59,130,246,0.25)", border: "1px solid rgba(59,130,246,0.3)", color: "#e0f2fe" }
                      : { background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.07)", color: "#d4d4d8" }
                    }
                  >
                    {m.text}
                  </div>
                  <p className="text-[9px] text-zinc-700 mt-0.5 px-1">{timeAgo(m.created_at)}</p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Reply input */}
          <div className="p-3 border-t border-white/[0.05] flex-shrink-0">
            <div className="flex items-end gap-2">
              <div className="flex-1 relative">
                <textarea
                  value={replyText}
                  onChange={e => setReplyText(e.target.value.slice(0, 500))}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                  placeholder="Antworten… (Enter zum Senden)"
                  rows={2}
                  data-testid="thread-reply-input"
                  className="w-full text-xs px-3 py-2 rounded-xl resize-none focus:outline-none focus:ring-1 focus:ring-blue-500/30 transition"
                  style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", color: "#d4d4d8" }}
                />
                <span className="absolute right-2 bottom-1.5 text-[9px] text-zinc-700">{replyText.length}/500</span>
              </div>
              <button
                onClick={sendReply}
                disabled={!replyText.trim() || sending}
                data-testid="thread-reply-send"
                className="w-8 h-8 flex items-center justify-center rounded-xl transition-all flex-shrink-0 disabled:opacity-40"
                style={{ background: "rgba(59,130,246,0.3)", border: "1px solid rgba(59,130,246,0.4)" }}
              >
                {sending ? <Loader2 size={13} className="animate-spin text-blue-300" /> : <Send size={13} className="text-blue-300" />}
              </button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

// ── Haupt-Glocke ───────────────────────────────────────────────────────────────
export function UserNotificationBell({ isAuth }) {
  const [open, setOpen]             = useState(false);
  const [bellTab, setBellTab]       = useState("push");
  const [pushCount, setPushCount]   = useState(0);
  const [msgCount, setMsgCount]     = useState(0);
  const [pushItems, setPushItems]   = useState([]);
  const [threads, setThreads]       = useState([]);
  const [openThread, setOpenThread] = useState(null);
  const [loadingPush, setLoadingPush] = useState(false);
  const [loadingMsg, setLoadingMsg]   = useState(false);
  const [pushStatus, setPushStatus] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const panelRef = useRef(null);

  const fetchPushCount = useCallback(async () => {
    if (!isAuth) return;
    try {
      const res = await api.get("/notifications/my/unread-count");
      setPushCount(res.data?.count ?? 0);
    } catch {}
  }, [isAuth]);

  const fetchMsgCount = useCallback(async () => {
    if (!isAuth) return;
    try {
      const res = await api.get("/messages/unread-count");
      setMsgCount(res.data?.count ?? 0);
    } catch {}
  }, [isAuth]);

  const fetchPushItems = useCallback(async () => {
    if (!isAuth) return;
    setLoadingPush(true);
    try {
      const res = await api.get("/notifications/my");
      setPushItems(res.data || []);
    } catch {}
    setLoadingPush(false);
  }, [isAuth]);

  const fetchThreads = useCallback(async () => {
    if (!isAuth) return;
    setLoadingMsg(true);
    try {
      const res = await api.get("/messages/threads");
      setThreads(res.data || []);
    } catch {}
    setLoadingMsg(false);
  }, [isAuth]);

  // Polling alle 60 Sekunden
  useEffect(() => {
    if (!isAuth) return;
    fetchPushCount();
    fetchMsgCount();
    const id = setInterval(() => { fetchPushCount(); fetchMsgCount(); }, 60_000);
    return () => clearInterval(id);
  }, [isAuth]); // eslint-disable-line

  const handleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      if (bellTab === "push") fetchPushItems();
      else fetchThreads();
    }
  };

  useEffect(() => {
    if (!open) return;
    if (bellTab === "push") fetchPushItems();
    else { fetchThreads(); setOpenThread(null); }
  }, [bellTab]); // eslint-disable-line

  useEffect(() => {
    const handler = e => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const markRead = async id => {
    await api.post("/notifications/my/mark-read", { id });
    setPushItems(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setPushCount(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await api.post("/notifications/my/mark-read", {});
    setPushItems(prev => prev.map(n => ({ ...n, read: true })));
    setPushCount(0);
  };

  const requestPush = async () => {
    const result = await Notification.requestPermission();
    setPushStatus(result);
  };

  const totalCount = pushCount + msgCount;

  if (!isAuth) return null;

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={handleOpen}
        data-testid="user-notification-bell"
        className="relative flex items-center justify-center w-9 h-9 rounded-xl transition-all duration-200 hover:bg-white/[0.07]"
        style={{ color: totalCount > 0 ? "#ec4899" : "#52525b" }}
        title="Benachrichtigungen & Nachrichten"
      >
        <Bell size={17} />
        {totalCount > 0 && (
          <span
            data-testid="user-notif-badge"
            className="absolute -top-1 -right-1 min-w-[17px] h-[17px] rounded-full flex items-center justify-center text-[10px] font-black text-white px-1"
            style={{ background: "linear-gradient(135deg,#ec4899,#be185d)", boxShadow: "0 2px 8px rgba(236,72,153,0.5)" }}
          >
            {totalCount > 99 ? "99+" : totalCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-11 w-80 rounded-2xl overflow-hidden z-50 flex flex-col"
          style={{
            background: "linear-gradient(180deg, #141414 0%, #0f0f0f 100%)",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "0 24px 60px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04) inset",
            maxHeight: "480px",
          }}
        >
          {/* Tabs */}
          <div className="flex items-center gap-0 border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
            <button
              onClick={() => setBellTab("push")}
              data-testid="bell-tab-push"
              className={`flex items-center gap-1.5 flex-1 px-3 py-3 text-xs font-semibold transition-all ${
                bellTab === "push" ? "text-rose-400" : "text-zinc-600 hover:text-zinc-300"
              }`}
              style={bellTab === "push" ? { borderBottom: "2px solid #fb7185" } : { borderBottom: "2px solid transparent" }}
            >
              <Bell size={12} />
              Push
              {pushCount > 0 && (
                <span className="text-[10px] font-black text-rose-400 bg-rose-400/10 px-1.5 py-0.5 rounded-full">{pushCount}</span>
              )}
            </button>
            <button
              onClick={() => setBellTab("messages")}
              data-testid="bell-tab-messages"
              className={`flex items-center gap-1.5 flex-1 px-3 py-3 text-xs font-semibold transition-all ${
                bellTab === "messages" ? "text-blue-400" : "text-zinc-600 hover:text-zinc-300"
              }`}
              style={bellTab === "messages" ? { borderBottom: "2px solid #60a5fa" } : { borderBottom: "2px solid transparent" }}
            >
              <MessageSquare size={12} />
              Nachrichten
              {msgCount > 0 && (
                <span className="text-[10px] font-black text-blue-400 bg-blue-400/10 px-1.5 py-0.5 rounded-full">{msgCount}</span>
              )}
            </button>
            {bellTab === "push" && pushItems.some(n => !n.read) && (
              <button onClick={markAllRead}
                className="text-zinc-500 hover:text-zinc-300 text-[11px] flex items-center gap-1 transition-colors px-3 flex-shrink-0">
                <Check size={10} /> Alle
              </button>
            )}
          </div>

          {/* ── TAB: Push ──────────────────────────────────────────────────── */}
          {bellTab === "push" && (
            <>
              {pushStatus === "default" && (
                <div className="px-4 py-3 flex items-center gap-3 flex-shrink-0"
                  style={{ background: "rgba(236,72,153,0.06)", borderBottom: "1px solid rgba(236,72,153,0.12)" }}>
                  <Bell size={13} className="text-rose-400 flex-shrink-0" />
                  <p className="text-rose-300/80 text-xs flex-1">Browser-Push aktivieren.</p>
                  <button onClick={requestPush}
                    className="text-[11px] font-bold text-rose-400 hover:text-rose-300 bg-rose-400/10 hover:bg-rose-400/20 px-2.5 py-1 rounded-lg transition-all flex-shrink-0">
                    Erlauben
                  </button>
                </div>
              )}
              {pushStatus === "denied" && (
                <div className="px-4 py-2.5 flex items-center gap-2 flex-shrink-0"
                  style={{ background: "rgba(239,68,68,0.06)", borderBottom: "1px solid rgba(239,68,68,0.1)" }}>
                  <X size={12} className="text-red-500 flex-shrink-0" />
                  <p className="text-red-400/70 text-[11px]">Push blockiert – in Browser-Einstellungen freigeben.</p>
                </div>
              )}
              <div className="overflow-y-auto flex-1" style={{ scrollbarWidth: "none" }}>
                {loadingPush ? (
                  <div className="flex justify-center py-8"><Loader2 size={16} className="animate-spin text-zinc-700" /></div>
                ) : pushItems.length === 0 ? (
                  <div className="py-10 text-center">
                    <Bell size={24} className="mx-auto mb-2 text-zinc-800" />
                    <p className="text-zinc-600 text-xs">Keine Benachrichtigungen</p>
                  </div>
                ) : pushItems.map(n => (
                  <UserNotifItem key={n.id} n={n} onRead={markRead} onClose={() => setOpen(false)} />
                ))}
              </div>
              <div className="px-4 py-2.5 flex items-center justify-center border-t flex-shrink-0"
                style={{ borderColor: "rgba(255,255,255,0.05)" }}>
                <Link to="/benachrichtigungen" onClick={() => setOpen(false)}
                  data-testid="notif-view-all-link"
                  className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors flex items-center gap-1">
                  Alle ansehen →
                </Link>
              </div>
            </>
          )}

          {/* ── TAB: Nachrichten ───────────────────────────────────────────── */}
          {bellTab === "messages" && (
            <div className="flex-1 overflow-hidden flex flex-col" style={{ minHeight: 0 }}>
              {openThread ? (
                <ThreadView
                  threadId={openThread}
                  onBack={() => { setOpenThread(null); fetchThreads(); fetchMsgCount(); }}
                  onClose={() => setOpen(false)}
                />
              ) : (
                <>
                  <div className="overflow-y-auto flex-1" style={{ scrollbarWidth: "none" }}>
                    {loadingMsg ? (
                      <div className="flex justify-center py-8"><Loader2 size={16} className="animate-spin text-zinc-700" /></div>
                    ) : threads.length === 0 ? (
                      <div className="py-10 text-center px-4">
                        <MessageSquare size={24} className="mx-auto mb-2 text-zinc-800" />
                        <p className="text-zinc-500 text-xs font-semibold mb-1">Noch keine Nachrichten</p>
                        <p className="text-zinc-700 text-[11px] leading-relaxed">
                          Schreib Artists oder Labels direkt über den Collab Finder an.
                        </p>
                      </div>
                    ) : threads.map(t => (
                      <ThreadItem key={t.id} thread={t} onClick={th => setOpenThread(th.id)} />
                    ))}
                  </div>
                  <div className="px-4 py-2.5 border-t flex-shrink-0"
                    style={{ borderColor: "rgba(255,255,255,0.05)" }}>
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] text-zinc-700 leading-relaxed">
                        Gespräche nur über aktive Collab-Gesuche starten
                      </p>
                      <Link
                        to="/nachrichten"
                        onClick={() => setOpen(false)}
                        data-testid="messages-view-all-link"
                        className="text-xs text-blue-500 hover:text-blue-300 transition-colors flex-shrink-0 ml-2"
                      >
                        Alle →
                      </Link>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
